import * as React from 'react'
import debounce from 'lodash.debounce'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ArrowDown01Icon,
  Cancel01Icon,
  Loading03Icon,
  Tick02Icon
} from '@hugeicons/core-free-icons'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'
import { Badge } from '@/components/ui/badge'

export interface AsyncComboboxOption<T = unknown> {
  label: string
  value: string
  data: T
}

export interface AsyncComboboxPage<T = unknown> {
  options: AsyncComboboxOption<T>[]
  loadMore?: () => Promise<AsyncComboboxPage<T>>
}

interface BaseProps<T> {
  loadOptions: (query: string) => Promise<AsyncComboboxPage<T>>
  disabled?: boolean
  required?: boolean
  id?: string
  'aria-label'?: string
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  debounceMs?: number
  /** Don't query until the search term reaches this length. Off (0) by default. */
  minSearchLength?: number
  onBlur?: () => void
  onFocus?: () => void
}

type SingleProps<T> = BaseProps<T> & {
  multiple?: false
  value: AsyncComboboxOption<T> | null
  onChange: (value: AsyncComboboxOption<T> | null) => void
}

type MultipleProps<T> = BaseProps<T> & {
  multiple: true
  value: AsyncComboboxOption<T>[]
  onChange: (value: AsyncComboboxOption<T>[]) => void
}

export type AsyncComboboxProps<T> = SingleProps<T> | MultipleProps<T>

export function AsyncCombobox<T>(props: AsyncComboboxProps<T>) {
  const {
    loadOptions,
    disabled,
    required,
    id,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    emptyText = 'No results found',
    debounceMs = 300,
    minSearchLength = 0,
    onBlur,
    onFocus
  } = props

  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState('')
  const [items, setItems] = React.useState<AsyncComboboxOption<T>[]>([])
  const [loadMore, setLoadMore] = React.useState<
    (() => Promise<AsyncComboboxPage<T>>) | undefined
  >(undefined)
  const [loading, setLoading] = React.useState(false)

  const listRef = React.useRef<HTMLDivElement>(null) // scroll container = observer root
  const sentinelRef = React.useRef<HTMLDivElement>(null) // bottom marker
  const loadingRef = React.useRef(false) // avoid firing while a page is in flight
  loadingRef.current = loading

  // Ignore responses that resolve out of order (stale search terms).
  const requestSeq = React.useRef(0)

  const selectedOptions = props.multiple
    ? props.value
    : props.value
    ? [props.value]
    : []

  const runLoad = React.useCallback(
    async (searchTerm: string) => {
      if (searchTerm.length < minSearchLength) {
        setItems([])
        setLoadMore(undefined)
        setLoading(false)
        return
      }
      const seq = ++requestSeq.current
      setLoading(true)
      try {
        const page = await loadOptions(searchTerm)
        if (seq !== requestSeq.current) return
        setItems(page.options)
        setLoadMore(() => page.loadMore)
      } finally {
        if (seq === requestSeq.current) setLoading(false)
      }
    },
    [loadOptions, minSearchLength]
  )

  const debouncedLoad = React.useMemo(
    () => debounce(runLoad, debounceMs),
    [runLoad, debounceMs]
  )

  React.useEffect(() => () => debouncedLoad.cancel(), [debouncedLoad])

  // Load default options when the popover opens.
  React.useEffect(() => {
    if (open) runLoad(query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  function handleQueryChange(next: string) {
    setQuery(next)
    debouncedLoad(next)
  }

  function emit(options: AsyncComboboxOption<T>[]) {
    if (props.multiple) {
      props.onChange(options)
    } else {
      props.onChange(options[0] ?? null)
    }
  }

  function toggle(option: AsyncComboboxOption<T>) {
    if (props.multiple) {
      const next = selectedOptions.some(o => o.value === option.value)
        ? selectedOptions.filter(o => o.value !== option.value)
        : [...selectedOptions, option]
      emit(next)
    } else {
      emit([option])
      setOpen(false)
    }
  }

  async function handleLoadMore() {
    if (!loadMore || loadingRef.current) return
    const seq = ++requestSeq.current
    setLoading(true)
    try {
      const page = await loadMore()
      if (seq !== requestSeq.current) return
      setItems(prev => [...prev, ...page.options])
      setLoadMore(() => page.loadMore)
    } finally {
      if (seq === requestSeq.current) setLoading(false)
    }
  }

  // Keep the latest handler so the observer doesn't re-subscribe every render.
  const handleLoadMoreRef = React.useRef(handleLoadMore)
  handleLoadMoreRef.current = handleLoadMore

  // Auto-load the next page when the bottom sentinel scrolls into view.
  React.useEffect(() => {
    if (!open || !loadMore) return
    const root = listRef.current
    const sentinel = sentinelRef.current
    if (!root || !sentinel) return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          handleLoadMoreRef.current()
        }
      },
      { root, rootMargin: '0px 0px 64px 0px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [open, loadMore])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          aria-label={props['aria-label']}
          aria-required={required}
          id={id}
          disabled={disabled}
          onBlur={onBlur}
          onFocus={onFocus}
          className='min-h-8 h-auto w-full justify-between gap-1 px-2 py-1 font-normal'
        >
          {props.multiple && selectedOptions.length > 0 ? (
            <span className='flex flex-1 flex-wrap items-center gap-1'>
              {selectedOptions.map((option, index) => (
                <Badge key={`${option.value}::${index}`} variant='secondary'>
                  {option.label}
                  {/* Pointer-only shortcut: keyboard/AT users deselect via the
                      option list, and a focusable control may not nest inside
                      the trigger button */}
                  <span
                    aria-hidden='true'
                    className='text-muted-foreground hover:text-foreground cursor-pointer'
                    onClick={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      toggle(option)
                    }}
                  >
                    <HugeiconsIcon
                      icon={Cancel01Icon}
                      strokeWidth={2}
                      className='size-3'
                    />
                  </span>
                </Badge>
              ))}
            </span>
          ) : !props.multiple && props.value ? (
            <span className='flex-1 truncate text-left'>
              {props.value.label}
            </span>
          ) : (
            <span className='text-muted-foreground flex-1 truncate text-left'>
              {placeholder}
            </span>
          )}
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            strokeWidth={2}
            className='size-3.5 text-muted-foreground shrink-0'
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-(--radix-popover-trigger-width) p-0'
        align='start'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={query}
            onValueChange={handleQueryChange}
          />
          <CommandList ref={listRef}>
            <CommandGroup>
              {items.map(option => {
                const isSelected = selectedOptions.some(
                  o => o.value === option.value
                )
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => toggle(option)}
                  >
                    <span
                      className={cn(
                        'size-3.5 flex shrink-0 items-center justify-center',
                        !isSelected && 'opacity-0'
                      )}
                    >
                      <HugeiconsIcon icon={Tick02Icon} strokeWidth={2} />
                    </span>
                    <span className='flex-1'>{option.label}</span>
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {loadMore && <div ref={sentinelRef} aria-hidden className='h-px' />}
            {loading && (
              <div className='text-muted-foreground flex items-center justify-center gap-2 py-4 text-xs'>
                <HugeiconsIcon
                  icon={Loading03Icon}
                  strokeWidth={2}
                  className='size-3.5 animate-spin'
                />
                Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className='text-muted-foreground py-4 text-center text-xs'>
                {query.length < minSearchLength
                  ? `Type at least ${minSearchLength} characters to search`
                  : emptyText}
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
